<template>
  <ul :class="containerClass" v-if="!noLiSurround">
    <li v-if="firstLastButton" :class="[pageClass, firstPageSelected() ? disabledClass : '']">
      <button class="border left rounded p-1" @click="selectFirstPage()" @keyup.enter="selectFirstPage()"
        :tabindex="firstPageSelected() ? -1 : 0" v-html="firstButtonText"></button>
    </li>

    <li v-if="!(firstPageSelected() && hidePrevNext)" :class="[prevClass, firstPageSelected() ? disabledClass : '']">
      <button @click="prevPage()" @keyup.enter="prevPage()" :class="prevLinkClass"
        :tabindex="firstPageSelected() ? -1 : 0">
        <img src="@/assets/images/previous-page.svg" />
      </button>
    </li>

    <li v-for="page in pages"
      :class="[pageClass, page.selected ? activeClass : '', page.disabled ? disabledClass : '', page.breakView ? breakViewClass : '']">
      <button v-if="page.breakView" :class="[pageLinkClass, breakViewLinkClass]" tabindex="0">
        <slot name="breakViewContent">{{ breakViewText }}</slot>
      </button>
      <button v-else-if="page.disabled" :class="pageLinkClass" tabindex="0">{{ page.content }}</button>
      <button v-else @click="handlePageSelected(page.index + 1)" @keyup.enter="handlePageSelected(page.index + 1)"
        :class="pageLinkClass" tabindex="0">{{ page.content }}</button>
    </li>

    <li v-if="!(lastPageSelected() && hidePrevNext)" :class="[nextClass, lastPageSelected() ? disabledClass : '']">
      <button @click="nextPage()" @keyup.enter="nextPage()" :class="nextLinkClass"
        :tabindex="lastPageSelected() ? -1 : 0">
        <img src="@/assets/images/previous-page.svg" />
      </button>
    </li>

    <li v-if="firstLastButton" :class="[pageClass, lastPageSelected() ? disabledClass : '']">
      <button @click="selectLastPage()" @keyup.enter="selectLastPage()" :class="pageLinkClass"
        :tabindex="lastPageSelected() ? -1 : 0" v-html="lastButtonText"></button>
    </li>
  </ul>

  <div :class="containerClass" v-else>
    <button v-if="firstLastButton" @click="selectFirstPage()" @keyup.enter="selectFirstPage()"
      :class="[prevLinkClass, firstPageSelected() ? disabledClass : '']" tabindex="0">
      <img src="@/assets/images/first-page.svg" />
    </button>
    <button v-if="!(firstPageSelected() && hidePrevNext)" @click="prevPage()" @keyup.enter="prevPage()"
      :class="[prevLinkClass, firstPageSelected() ? disabledClass : '']" tabindex="0">
      <img src="@/assets/images/previous-page.svg" />
    </button>

    <template v-for="page in pages">
      <button v-if="page.breakView" :class="[pageLinkClass, breakViewLinkClass, page.disabled ? disabledClass : '']"
        tabindex="0">
        <slot name="breakViewContent">{{ breakViewText }}</slot>
      </button>
      <button v-else-if="page.disabled" :class="[pageLinkClass, page.selected ? activeClass : '', disabledClass]"
        tabindex="0">{{ page.content }}</button>
      <button v-else @click="handlePageSelected(page.index)" @keyup.enter="handlePageSelected(page.index)"
        :class="[pageLinkClass, page.selected ? activeClass : '', this.invalidPageNumbers.includes(page.index +1) ? 'invalid' : '']" tabindex="0">{{ page.content }}</button>
    </template>

    <button v-if="!(lastPageSelected() && hidePrevNext)" @click="nextPage()" @keyup.enter="nextPage()"
      :class="[nextLinkClass, lastPageSelected() ? disabledClass : '']" tabindex="0">
      <img src="@/assets/images/next-page.svg" />
    </button>
    <button v-if="firstLastButton" @click="selectLastPage()" @keyup.enter="selectLastPage()"
      :class="[nextLinkClass, lastPageSelected() ? disabledClass : '']" tabindex="0">
      <img src="@/assets/images/last-page.svg" />
    </button>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Number
    },
    pageCount: {
      type: Number,
      required: true
    },
    forcePage: {
      type: Number
    },
    clickHandler: {
      type: Function,
      default: () => { }
    },
    pageRange: {
      type: Number,
      default: 3
    },
    marginPages: {
      type: Number,
      default: 1
    },
    prevText: {
      type: String,
      default: 'Prev'
    },
    nextText: {
      type: String,
      default: 'Next'
    },
    breakViewText: {
      type: String,
      default: '…'
    },
    containerClass: {
      type: String
    },
    pageClass: {
      type: String
    },
    pageLinkClass: {
      type: String
    },
    prevClass: {
      type: String
    },
    prevLinkClass: {
      type: String
    },
    nextClass: {
      type: String
    },
    nextLinkClass: {
      type: String
    },
    breakViewClass: {
      type: String
    },
    breakViewLinkClass: {
      type: String
    },
    activeClass: {
      type: String,
      default: 'active'
    },
    disabledClass: {
      type: String,
      default: 'disabled'
    },
    noLiSurround: {
      type: Boolean,
      default: true
    },
    firstLastButton: {
      type: Boolean,
      default: false
    },
    firstButtonText: {
      type: String,
      default: 'First'
    },
    lastButtonText: {
      type: String,
      default: 'Last'
    },
    hidePrevNext: {
      type: Boolean,
      default: false
    },
    invalidPageNumbers: {
      type: Array,
      default: []
    }
  },
  beforeUpdate() {
    if (this.forcePage === undefined) return
    if (this.forcePage !== this.selected) {
      this.selected = this.forcePage
    }
  },
  computed: {
    selected: {
      get: function () {
        return this.value ?? this.innerValue
      },
      set: function (newValue) {
        this.innerValue = newValue
      }
    },
    pages: function () {
      let items = {}
      if (this.pageCount <= this.pageRange) {
        for (let index = 0; index < this.pageCount; index++) {
          let page = {
            index: index,
            content: index + 1,
            invalid: this.invalidPageNumbers.includes(this.innerValue),
            selected: index === this.selected
          }
          items[index] = page
        }
      } else {
        const halfPageRange = Math.floor(this.pageRange / 2) - 1

        let setPageItem = index => {
          let page = {
            index: index,
            content: index + 1,
            selected: index === (this.selected)
          }

          items[index] = page
        }

        let setBreakView = index => {
          let breakView = {
            disabled: false,
            breakView: false
          }

          items[index] = breakView
        }

        // 1st - loop thru low end of margin pages
        for (let i = 0; i < this.marginPages; i++) {
          setPageItem(i);
        }

        // 2nd - loop thru selected range
        let selectedRangeLow = 0;
        if (this.selected - halfPageRange > 0) {
          selectedRangeLow = this.selected - 1 - halfPageRange;
        }

        let selectedRangeHigh = selectedRangeLow + this.pageRange - 1;
        if (selectedRangeHigh >= this.pageCount) {
          selectedRangeHigh = this.pageCount - 1;
          selectedRangeLow = selectedRangeHigh - this.pageRange + 1;
        }

        for (let i = selectedRangeLow; i <= selectedRangeHigh && i <= this.pageCount - 1; i++) {
          setPageItem(i);
        }

        // Check if there is breakView in the left of selected range
        if (this.marginPages && selectedRangeLow > this.marginPages) {
          setBreakView(selectedRangeLow - 1)
        }

        // Check if there is breakView in the right of selected range
        if (this.marginPages && selectedRangeHigh + 1 < this.pageCount - this.marginPages) {
          setBreakView(selectedRangeHigh + 1)
        }

        // 3rd - loop thru high end of margin pages
        for (let i = this.pageCount - 1; i >= this.pageCount - this.marginPages; i--) {
          setPageItem(i);
        }
      }
      return items
    }
  },
  data() {
    return {
      innerValue: 1,
    }
  },
  methods: {
    handlePageSelected(selected) {
      if (this.selected === selected || selected === this.pageCount) return

      this.innerValue = selected
      this.$emit('input', selected)
      this.clickHandler(selected)
    },
    prevPage() {
      if (this.selected <= 0) return

      this.handlePageSelected(this.selected - 1)
    },
    nextPage() {
      if (this.selected >= this.pageCount) return

      this.handlePageSelected(this.selected + 1)
    },
    firstPageSelected() {
      return this.selected === 1
    },
    lastPageSelected() {
      return (this.selected === this.pageCount) || (this.pageCount === 0)
    },
    selectFirstPage() {
      if (this.selected <= 0) return

      this.handlePageSelected(0)
    },
    selectLastPage() {
      if (this.selected >= this.pageCount) return

      this.handlePageSelected(this.pageCount)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.button-group {
  display: flex;
  width: 100%;
  justify-content: end;
  align-items: center;
  gap: 4px;

  button {
    width: 32px;
    height: 32px;
    border-style: solid;
    gap: 0px;
    border-radius: 6px;
    border-width: 0.5px;
    border-color: $colorD30;
    color: $colorN70;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 12px;
    padding-left: 12px;

    &.invalid {
      border: 1px solid $colorE50;
      color: $colorE50;
      background-color: $colorE05;
    }

    &.active.invalid {
      border: 2px solid $colorE50 !important;
    }

    &.active {
      background: $colorN80;
      color: $colorN00;
      border-color: transparent;
    }

    &.left {
      border-radius: 6px 2px 2px 6px;
    }

    &:not(.right):not(.left):not(.disabled) {
      border-width: 1px;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05000000074505806px;
      text-align: center;
    }

    &.right {
      border-radius: 2px 6px 6px 2px;
    }
  }
}
</style>
